import React, { useCallback, useMemo } from "react";
import PostAddForm from "../../../../components/forms/postItemsForm/index";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SuccessNotify } from "../../../../constants/tostify";
import { FailedNotify } from "../../../../constants/tostify";
import { useSelector } from "react-redux";
import Loader from "../../../../components/Loader";
import { publishImage } from "../../../../helpers/imageupload";
import { useDropzone } from "react-dropzone";
import { decodeId } from "../../../../utils/idObfuscator";
import {
  useGetShopByIdQuery,
  useLazyGetShopCategoriesQuery,
  useCreateShopItemMutation,
} from "../../../../features/shop/shopApiSlice";

/** Default top-level gems parent when shop.shopCategory is missing */
const GEM_DEFAULT_PARENT = "Main catagory Gems&jewelry";

const MineralMinnersPostAdd = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isloading, setIsloading] = useState(false);
  const imageUrl = useSelector((state) => state.imgurl);
  const { name } = useSelector((state) => state.user);
  const [displayimg, setDisplayimg] = useState();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [data, setData] = useState({
    category: "",
    subCategory: "",
  });
  const [shopCategory, setShopCategory] = useState([]);
  const [shopCategorytwo, setShopCategorytwo] = useState([]);
  const [parentCategory, setParentCategory] = useState("");

  const [triggerGetCategories] = useLazyGetShopCategoriesQuery();
  const [createShopItem] = useCreateShopItemMutation();

  const cleanShopId = decodeId(id) || id || "";
  const { data: shopRes } = useGetShopByIdQuery(cleanShopId, { skip: !cleanShopId });
  const shop = shopRes?.data?.Shop;

  const Handledata = (e) => {
    const raw = e.target.value || "";
    // value format: `${_id}.${name}` — only split on the first dot
    const dot = raw.indexOf(".");
    let idPart = raw;
    let namePart = "";
    if (dot !== -1) {
      idPart = raw.slice(0, dot);
      namePart = raw.slice(dot + 1);
      try {
        namePart = decodeURIComponent(namePart);
      } catch (_) {
        /* keep raw name */
      }
    }
    const parts = [idPart, namePart];
    setData((prevState) => ({
      ...prevState,
      [e.target.name]: parts,
      // reset subcategory when category changes
      ...(e.target.name === "category" ? { subCategory: "" } : {}),
    }));
    if (e.target.name === "category") {
      setShopCategorytwo([]);
    }
  };

  const categoryName = data?.category?.[1] || "";
  const categoryid = data?.category?.[0] || "";
  const subcategoryName = data?.subCategory?.[1] || "";
  const subcategoryid = data?.subCategory?.[0] || "";

  // Resolve parent category from this shop
  useEffect(() => {
    const fromShop =
      shop?.shopCategory?.name ||
      shop?.shopCategory ||
      "";
    const resolved =
      (typeof fromShop === "string" && fromShop.trim()) ||
      GEM_DEFAULT_PARENT;

    setParentCategory(resolved);
  }, [shop]);

  const loadCategories = useCallback(
    async (parent) => {
      const parentKey = (parent || "").trim();
      if (!parentKey) {
        setShopCategory([]);
        return;
      }
      try {
        const response = await triggerGetCategories({
          type: "gemShop",
          parentCategory: parentKey,
        }).unwrap();
        if (response?.success) {
          setShopCategory(response?.data?.Category || []);
          return;
        }
      } catch (_) {
        // fall through to top-level fetch
      }

      // Fallback: shop has no/invalid shopCategory — show top-level gemShop cats
      try {
        const fallback = await triggerGetCategories({
          type: "gemShop",
          parentCategory: "",
        }).unwrap();
        if (fallback?.success) {
          setShopCategory(fallback?.data?.Category || []);
        } else {
          setShopCategory([]);
        }
      } catch (error) {
        console.log("gemShop categories", error);
        setShopCategory([]);
      }
    },
    [triggerGetCategories],
  );

  const loadSubCategories = useCallback(
    async (parentName) => {
      const parentKey = (parentName || "").trim();
      if (!parentKey) {
        setShopCategorytwo([]);
        return;
      }

      const fetchCats = async (type, parent) => {
        try {
          const response = await triggerGetCategories({
            type,
            parentCategory: parent,
          }).unwrap();
          if (response?.success && Array.isArray(response?.data?.Category)) {
            return response.data.Category;
          }
        } catch (_) {
          /* try next */
        }
        return [];
      };

      // 1) Normal: gemShopItem children of selected category
      //    e.g. parent "Item catagory Gems&jewelry"
      let subs = await fetchCats("gemShopItem", parentKey);

      // 2) If user picked the Main parent as "category", resolve via its
      //    gemShop child (Item catagory...) then load that child's items
      if (!subs.length) {
        const midLevel = await fetchCats("gemShop", parentKey);
        if (midLevel.length === 1) {
          subs = await fetchCats("gemShopItem", midLevel[0].name);
        } else if (midLevel.length > 1) {
          // show mid-level as selectable sub-options when item-level is empty
          subs = midLevel;
        }
      }

      // 3) Last resort: known gems item parent
      if (!subs.length && parentKey !== "Item catagory Gems&jewelry") {
        subs = await fetchCats("gemShopItem", "Item catagory Gems&jewelry");
      }

      setShopCategorytwo(subs);
    },
    [triggerGetCategories],
  );

  // Auto-load category list when parent is known
  useEffect(() => {
    if (parentCategory) {
      loadCategories(parentCategory);
    }
  }, [parentCategory, loadCategories]);

  // Auto-load subcategories when a category is chosen
  useEffect(() => {
    if (categoryName) {
      loadSubCategories(categoryName);
    } else {
      setShopCategorytwo([]);
    }
  }, [categoryName, loadSubCategories]);

  const onDrop = async (acceptedFiles) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);

      const abc = await publishImage(acceptedFiles, (percent) => {
        setUploadProgress(percent);
      });
      setUserData((prev) => ({
        ...prev,
        images: abc,
      }));

      setDisplayimg(abc);
    } catch (error) {
      FailedNotify("Failed! Try Again");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*",
    onDrop,
  });

  const [userData, setUserData] = useState({
    businessType: "gemShop",
    userid: "",
    shopid: "",
    isSingleItem: false,
    isScrapItem: false,
    itemname: "",
    category: "",
    categoryid: "",
    subCategory: "",
    subCategoryid: "",
    price: "",
    country: "",
    city: "",
    state: "",
    quantity: "",
    unitOfMeasurement: "",
    currency: {
      currencyName: "",
    },
    images: [""],
    status: true,
  });

  useEffect(() => {
    setUserData((prev) => ({
      ...prev,
      category: categoryName,
      categoryid: categoryid,
      subCategory: subcategoryName,
      subCategoryid: subcategoryid,
    }));
  }, [categoryid, categoryName, subcategoryName, subcategoryid]);

  useEffect(() => {
    setUserData((prev) => ({
      ...prev,
      images: imageUrl?.imgurl || prev.images,
    }));
  }, [imageUrl]);

  useEffect(() => {
    const resolvedUserId = shop?.userid?._id || shop?.userid || name?._id || "";
    const resolvedShopId = shop?._id || cleanShopId || id || "";
    if (!resolvedUserId && !resolvedShopId) return;

    // Load country, state, city from backend API (shop or user profile)
    const defaultCountry =
      shop?.resolvedCountry ||
      shop?.country ||
      shop?.userid?.address?.country ||
      shop?.userid?.country ||
      name?.address?.country ||
      name?.country ||
      "";
    const defaultState =
      shop?.resolvedRegion ||
      shop?.state ||
      shop?.userid?.address?.state ||
      shop?.userid?.state ||
      name?.address?.state ||
      name?.state ||
      "";
    const defaultCity =
      shop?.resolvedCity ||
      shop?.city ||
      shop?.userid?.address?.city ||
      shop?.userid?.city ||
      name?.address?.city ||
      name?.city ||
      name?.MCity ||
      "";

    setUserData((prev) => ({
      ...prev,
      userid: resolvedUserId || prev.userid,
      shopid: resolvedShopId || prev.shopid,
      country: prev.country || defaultCountry,
      state: prev.state || defaultState,
      city: prev.city || defaultCity,
    }));
  }, [shop, name, cleanShopId, id]);

  const FirstCategory = () => {
    loadCategories(parentCategory || GEM_DEFAULT_PARENT);
  };

  const Mydata = () => {
    loadSubCategories(categoryName);
  };

  const userDatahandler = async (e) => {
    e.preventDefault();
    setIsloading(true);
    try {
      const response = await createShopItem({
        ...userData,
        userid: userData.userid || shop?.userid?._id || shop?.userid || name?._id,
        shopid: userData.shopid || shop?._id || cleanShopId || id,
        country: userData.country,
        state: userData.state,
        city: userData.city,
        businessType: "gemShop",
      }).unwrap();

      if (response?.success) {
        setTimeout(() => {
          SuccessNotify("Add Post Successfully");
        }, 1000);
        navigate(`/minerals-and-mining-profile/${id}`);
      } else {
        FailedNotify("Failed To Post Try Again!");
      }
    } catch (err) {
      FailedNotify("Failed To Post Try Again!");
    } finally {
      setIsloading(false);
    }
  };

  const categoryOptions = useMemo(
    () => (Array.isArray(shopCategory) ? shopCategory : []),
    [shopCategory],
  );
  const subCategoryOptions = useMemo(
    () => (Array.isArray(shopCategorytwo) ? shopCategorytwo : []),
    [shopCategorytwo],
  );

  if (isloading) {
    return <Loader heightsetting="screenHeight" />;
  }

  return (
    <PostAddForm
      postbtn="/minerals-and-mining-profile"
      Handledata={Handledata}
      Mydata={Mydata}
      data={data}
      shopCategorytwo={subCategoryOptions}
      shopCategory={categoryOptions}
      butn="Post"
      cancel="/minerals-and-mining-profile/"
      back="/minerals-and-mining-profile/"
      routename="/minerals-Minning-PostAdd"
      setUserData={setUserData}
      handlerFunction={userDatahandler}
      userData={userData}
      id={id}
      getInputProps={getInputProps}
      getRootProps={getRootProps}
      displayimg={displayimg}
      setDisplayimg={setDisplayimg}
      FirstCategory={FirstCategory}
      isUploading={isUploading}
      uploadProgress={uploadProgress}
    />
  );
};

export default MineralMinnersPostAdd;
